import { Extension } from '@tiptap/core'
import { Fragment, Node } from '@tiptap/pm/model'
import {
  Allow,
  parse as parsePartialJson,
  PartialJSON,
  isDeepEqualData,
} from '@deepguide-ai/shared'
import { aiSuggestionPluginKey } from './ai-suggestion/plugin'
import { ApplyAiSuggestionOptions } from './ai-suggestion/types'

// Helper function to check if a node type is atomic (doesn't have content)
function isAtomicNodeType(nodeType: string): boolean {
  const atomicTypes = ['Screenshot']
  return atomicTypes.includes(nodeType)
}

function cleanContentNodes(nodes: any): any {
  if (Array.isArray(nodes)) {
    return nodes
      .map((node) => cleanContentNodes(node))
      .filter((node) => {
        if (!node) return false
        if (
          node.type === 'text' &&
          (typeof node.text !== 'string' || node.text === '')
        )
          return false
        // Allow atomic nodes like Screenshot that don't have content
        if (node.type !== 'text' && !node.content && !isAtomicNodeType(node.type)) return false
        return true
      })
  } else if (nodes && typeof nodes === 'object') {
    if (!nodes.type) return null
    if (nodes.type === 'text') {
      if (typeof nodes.text !== 'string' || nodes.text === '') {
        return null
      }
      // Clean marks: only keep marks that are objects with a defined 'type'
      if (Array.isArray(nodes.marks)) {
        nodes.marks = nodes.marks.filter(
          (mark: any) =>
            mark && typeof mark === 'object' && typeof mark.type === 'string'
        )
      }
      return nodes
    }
    if (nodes.type === 'doc') {
      if (Array.isArray(nodes.content)) {
        const childrenContent = cleanContentNodes(nodes.content)
        if (childrenContent.length === 0) {
          return null
        }
        return {
          ...nodes,
          content: childrenContent,
        }
      }
      return null
    }
    
    // Handle atomic nodes like Screenshot that don't have content
    if (isAtomicNodeType(nodes.type)) {
      return nodes // Return atomic nodes as-is
    }
    
    const childrenContent = cleanContentNodes(nodes.content)
    if (!childrenContent) {
      return null
    }
    if (Array.isArray(childrenContent) && childrenContent.length === 0) {
      return null
    }
    return {
      ...nodes,
      content: cleanContentNodes(nodes.content),
    }
  }
  return nodes
}

// Enhanced JSON parsing with better debugging
function parseAndFixContent(
  jsonChunk: string,
  allowPartial: number
): any[] | null {
  try {
    let parsed = parsePartialJson(jsonChunk, allowPartial || Allow.ALL)
    parsed = cleanContentNodes(parsed)

    if (!Array.isArray(parsed)) {
      // If it's a root doc node, extract its content
      if (
        parsed &&
        typeof parsed === 'object' &&
        parsed.type === 'doc'
        // && Array.isArray(parsed.content)
      ) {
        parsed = parsed.content
      } else if (
        parsed &&
        typeof parsed === 'object' &&
        (('type' in parsed && 'content' in parsed) || 'text' in parsed)
      ) {
        parsed = [parsed]
      } else {
        return null
      }
    }

    return parsed
  } catch (error) {
    if (error instanceof PartialJSON) {
      console.warn('🔍 Partial JSON error, waiting for more content')
      return null
    }
    // const parseError = error as Error
    // console.error('❌ JSON parsing error:', parseError)
    return null
  }
}

function applyOptionsToNode(node: any, options: any): any {
  if (!node || typeof node !== 'object') {
    return node
  }

  const processedNode = { ...node }

  // Apply element props
  if (options.elementProps && processedNode.type) {
    processedNode.attrs = {
      ...processedNode.attrs,
      ...options.elementProps,
    }
  }

  // Handle text marks (Tiptap uses marks array)
  if (processedNode.type === 'text' && options.textProps) {
    if (!processedNode.marks) {
      processedNode.marks = []
    }

    // Convert text props to marks
    Object.entries(options.textProps).forEach(([key, value]) => {
      if (
        value &&
        !processedNode.marks.some((mark: any) => mark.type === key)
      ) {
        processedNode.marks.push({
          type: key,
          attrs: typeof value === 'object' ? value : {},
        })
      }
    })
  }

  // Recursively process content
  if (processedNode.content && Array.isArray(processedNode.content)) {
    processedNode.content = processedNode.content.map((child: any) =>
      applyOptionsToNode(child, options)
    )
  }

  return processedNode
}

// Enhanced node creation with detailed debugging
function safeCreateNode(state: any, nodeJson: any): any {
  if (!nodeJson || !nodeJson.type) {
    console.warn('❌ Skipping node with missing type:', nodeJson)
    return null
  }
  
  // Log all Screenshot nodes entering safeCreateNode
  if (nodeJson.type === 'Screenshot') {
    console.log(`🔍 Screenshot node entering safeCreateNode:`, nodeJson)
  }
  try {
    // Check if node type exists in schema
    if (nodeJson.type && !state.schema.nodes[nodeJson.type]) {
      return null
    }

    if (nodeJson.type === 'text' && nodeJson.text === '') {
      return null
    }

    // Don't filter out atomic nodes like Screenshot that don't have content
    if (nodeJson.type !== 'text' && !nodeJson.content && !isAtomicNodeType(nodeJson.type)) {
      console.log(`❌ Filtering out node without content:`, nodeJson)
      return null
    }

    const node = state.schema.nodeFromJSON(nodeJson)
  
    return node
  } catch (error) {
    console.warn(`❌ Failed to create node from JSON:`, {
      nodeJson,
      error: error instanceof Error ? error.message : error,
      availableNodes: Object.keys(state.schema.nodes),
    })
    return null
  }
}

// Helper functions for smart diffing (adapted from Plate.js)
function getNodeType(node: any): string {
  if (isTextNode(node)) return 'text'
  if (isElementNode(node)) return `element:${node.type}`
  return 'unknown'
}

function isTextNode(node: any): boolean {
  return node && typeof node === 'object' && 'text' in node && !('type' in node)
}

function isElementNode(node: any): boolean {
  return node && typeof node === 'object' && 'type' in node
}

function getNodeProps(node: any): Record<string, any> {
  if (!node || typeof node !== 'object') return {}
  const { content, text, type, ...props } = node
  return props
}

// Check if this is a container element that should use children diff (adapted from Plate.js)
function isContainerElement(elementType: string): boolean {
  const containerTypes = [
    'Steps',
    'Step',
    'ul',
    'ol',
    'blockquote',
    'table',
    'tr',
    'td',
    'th',
    'Card',
    'CardGroup',
    'Step',
    'Tab',
    'Tabs',
    'Accordion',
    'AccordionGroup',
    'doc',
    'paragraph',
    'heading',
  ]
  return containerTypes.includes(elementType)
}

function findNodePosition(
  doc: any,
  basePos: number,
  nodeIndex: number
): number | null {
  try {
    let pos = basePos
    let currentIndex = 0

    while (pos < doc.content.size && currentIndex < nodeIndex) {
      const $pos = doc.resolve(pos)
      if ($pos.nodeAfter) {
        pos += $pos.nodeAfter.nodeSize
        currentIndex++
      } else {
        break
      }
    }

    return currentIndex === nodeIndex ? pos : null
  } catch (_error) {
    return null
  }
}

// Types matching TipTap's official API but with content type support
export type StreamContentRange = number | { from: number; to: number }

export interface StreamContentWriteContext {
  /**
   * The partial content of the stream to insert.
   */
  partial: string
  /**
   * Content type to determine how to parse the partial content
   */
  contentType?: 'json' | 'markdown' | 'html' | 'text'
  /**
   * This function allows you to transform the content before inserting it into the editor.
   * It must return a Prosemirror `Fragment` or `Node`.
   */
  transform?: (ctx: {
    /**
     * The accumulated content of the stream.
     */
    buffer: string
    /**
     * The current partial content of the stream.
     */
    partial: string
    editor: any
    /**
     * Allows you to use the default transform function.
     */
    defaultTransform: (
      /**
       * The content to process based on contentType
       * @default ctx.buffer
       */
      content?: string
    ) => Fragment | Node | Node[] | null
  }) => Fragment | Node | Node[] | null
  /**
   * Allows you to append commands to the chain before it is executed.
   */
  appendToChain?: (chain: any) => any
}

export interface StreamContentAPI {
  /**
   * The function to write content into the editor.
   */
  write: (ctx: StreamContentWriteContext) => Promise<{
    /**
     * The buffer that is being written to.
     */
    buffer: string
    /**
     * The start of the inserted content in the editor.
     */
    from: number
    /**
     * The end of the inserted content in the editor.
     */
    to: number
  } | null>
  /**
   * A writable stream to write content into the editor.
   * @example fetch('https://example.com/stream').then(response => response.body.pipeTo(ctx.getWritableStream()))
   */
  getWritableStream: () => WritableStream
}

export interface StreamContentOptions {
  parseOptions?: any
  /**
   * This will insert the content at the same depth as the `from` position.
   * Effectively, this will insert the content as a sibling of the node at the `from` position.
   * @default true
   */
  respondInline?: boolean
  /**
   * Default content type for all writes in this stream
   */
  defaultContentType?: 'json' | 'markdown' | 'html' | 'text'
}

// Helper functions for content parsing
function parseContent(
  content: string,
  contentType: string,
  state: any
): Fragment | Node | Node[] | null {
  let parsed: any[] | null = null
  let nodes: Node[] = []

  switch (contentType) {
    case 'json':
      // Use our sophisticated JSON parsing
      parsed = parseAndFixContent(content, Allow.ALL)

      if (!parsed || parsed.length === 0) {
        console.warn('⚠️ JSON parsing failed, falling back to text')
        // return createTextNode(state, content)
        return null
      }

      nodes = parsed
        .map((nodeData) => {
          const node = safeCreateNode(state, nodeData)
          return node
        })
        .filter(Boolean) as Node[]

      if (nodes.length === 0) {
        console.warn('⚠️ No valid JSON nodes created, falling back to text')
        // return createTextNode(state, content)
        return null
      }

      return nodes

    case 'html':
      // For HTML, use the editor's HTML parser
      try {
        const htmlDoc = new DOMParser().parseFromString(content, 'text/html')
        const fragment = state.schema.parseSlice(htmlDoc.body, {
          preserveWhitespace: true,
        })
        return fragment.content
      } catch (error) {
        console.error('❌ HTML parse error:', error)
        return createTextNode(state, content)
      }

    case 'markdown':
      // TODO:
      // For markdown, we'd need a markdown parser
      // For now, treat as text and let the editor handle it
      return createTextNode(state, content)

    case 'text':
    default:
      return createTextNode(state, content)
  }
}

function createTextNode(state: any, text: string): Node[] {
  if (!text.trim()) {
    return []
  }

  try {
    const node = state.schema.nodes.paragraph.create(
      {},
      state.schema.text(text)
    )

    return [node]
  } catch (error) {
    console.error('❌ createTextNode failed:', error)
    return []
  }
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    streamContent: {
      /**
       * Stream content into the editor at the specified position
       */
      streamContent: (
        position: StreamContentRange,
        /**
         * The callback to write the content into the editor.
         */
        callback: (options: StreamContentAPI) => Promise<any>,
        /**
         * The options to configure streaming behavior.
         */
        options?: StreamContentOptions
      ) => ReturnType
    }
  }
}

export interface StreamContentExtensionStorage {
  isStreamingJson: boolean
  blockChunks: string
  blockContent: any[] | null // The parsed content
  blockPath: number[] | null
  streamingStartTime: number
  transactionQueue: Array<() => Promise<void>>
  isProcessingQueue: boolean
  processTransactionQueue: () => Promise<void>
}

export interface StreamContentExtensionOptions {
  /**
   * Whether to disable the stream content extension.
   * @default false
   */
  disabled?: boolean
}

export const StreamContentExtension =
  Extension.create<StreamContentExtensionOptions>({
    name: 'streamContent',

    addOptions() {
      return {
        disabled: false,
      }
    },

    addStorage() {
      const storage = {
        // JSON streaming state
        isStreamingJson: false,
        blockChunks: '', // the raw JSON string
        blockContent: null, // the parsed content (array of nodes)
        blockPath: null as number[] | null,
        streamingStartTime: 0,
        // General streaming state (tracks any streaming activity)
        isStreaming: false,
        // Transaction queue to prevent race conditions
        transactionQueue: [] as Array<() => Promise<void>>,
        isProcessingQueue: false,

        processTransactionQueue: async () => {
          if (storage.isProcessingQueue) return

          storage.isProcessingQueue = true

          const processNext = async () => {
            if (storage.transactionQueue.length === 0) {
              storage.isProcessingQueue = false
              return
            }

            const nextTransaction = storage.transactionQueue.shift()
            if (nextTransaction) {
              try {
                await nextTransaction()
              } catch (error) {
                console.error('❌ Transaction queue error:', error)
              }
            }

            setTimeout(processNext, 0)
          }

          processNext()
        },
      } as StreamContentExtensionStorage
      return storage
    },

    addCommands() {
      return {
        streamContent:
          (
            position: StreamContentRange | ((editor: any) => number),
            callback: (api: StreamContentAPI) => Promise<any>,
            options: StreamContentOptions = {}
          ) =>
          ({ editor }) => {
            // Parse position
            let from: number, to: number
            if (typeof position === 'function') {
              from = position(editor)
              to = from
            } else if (typeof position === 'number') {
              from = position
              to = position
            } else {
              from = position.from
              to = position.to
            }

            // Initialize streaming state
            let buffer = ''
            const streamFrom = from
            let streamTo = from
            let hasReplacedRange = false
            const defaultContentType = options.defaultContentType || 'text'
            
            // Set general streaming flag to prevent auto-selection
            this.storage.isStreaming = true

            // Fix transaction queue access
            const queueTransaction = async (
              transactionFn: (freshState: any, freshDispatch: any) => void
            ) => {
              return new Promise<void>((resolve, reject) => {
                this.storage.transactionQueue.push(async () => {
                  try {
                    const freshState = editor.state
                    const freshDispatch = editor.view.dispatch
                    transactionFn(freshState, freshDispatch)
                    resolve()
                  } catch (error) {
                    reject(error)
                  }
                })
                if (!this.storage.isProcessingQueue) {
                  this.storage.processTransactionQueue()
                }
              })
            }

            // Create the write API
            const writeAPI: StreamContentAPI = {
              write: async (writeContext: StreamContentWriteContext) => {
                const {
                  partial,
                  contentType: ctxContentType,
                  transform,
                  appendToChain,
                } = writeContext

                // Determine content type first
                const contentType = ctxContentType || defaultContentType

                buffer += partial

                // CRITICAL: Always use the current editor state instead of captured state
                const currentState = editor.state

                if (contentType === 'json') {
                  // Parse the new content
                  const newContent = parseAndFixContent(buffer, Allow.ALL)

                  if (!newContent || newContent.length === 0) {
                    return {
                      buffer,
                      from: streamFrom,
                      to: streamTo,
                    }
                  }

                  // Parse previous content if any
                  const previousContent = this.storage.blockContent || null

                  if (!this.storage.isStreamingJson) {
                    this.storage.isStreamingJson = true
                    // Initial insertion - replace range if needed and start streaming

                    if (!hasReplacedRange && from !== to) {
                      await queueTransaction((freshState, freshDispatch) => {
                        let currentFrom = from
                        let currentTo = to
                        // Defensive: recalculate if at end of doc
                        if (currentFrom > freshState.doc.content.size) {
                          currentFrom = freshState.doc.content.size
                        }
                        if (currentTo > freshState.doc.content.size) {
                          currentTo = freshState.doc.content.size
                        }
                        if (currentFrom < 0) currentFrom = 0
                        if (currentTo < 0) currentTo = 0
                        if (currentFrom > currentTo) currentFrom = currentTo
                        const tr = freshState.tr.delete(currentFrom, currentTo)
                        freshDispatch(tr)
                      })
                      // Update position after deletion
                      streamTo = streamFrom
                      hasReplacedRange = true
                    }

                    // Insert initial content at position

                    const processedContent = newContent.map((node) =>
                      applyOptionsToNode(node, {})
                    )

                    const tr = currentState.tr

                    try {
                      let currentPos = streamFrom
                      // let insertedNodes = 0

                      processedContent.forEach((nodeJson, index) => {
                        const node = safeCreateNode(currentState, nodeJson)

                        if (node) {
                          // const tr = currentState.tr

                          tr.insert(currentPos, node)
                          currentPos += node.nodeSize
                          // insertedNodes++
                        } else {
                          console.warn(
                            `❌ Failed to create node ${index + 1}:`,
                            nodeJson
                          )
                        }
                      })

                      await queueTransaction(() => {
                        const freshDispatch = editor.view.dispatch
                        // Don't set selection during streaming to prevent auto-selection of generated content
                        freshDispatch(tr)
                      })
                      streamTo = currentPos

                      this.storage.isStreamingJson = true
                      this.storage.blockChunks = buffer
                      this.storage.blockContent = newContent
                      this.storage.blockPath = [streamFrom]
                      this.storage.streamingStartTime = Date.now()
                    } catch (error) {
                      this.storage.isStreamingJson = false // reset streaming mode
                      console.warn('❌ Initial JSON insertion failed:', error)
                      return {
                        buffer,
                        from: streamFrom,
                        to: streamTo,
                      }
                    }
                  } else {
                    // Incremental update with smart diffing
                    if (!previousContent) {
                      this.storage.blockChunks = buffer
                      this.storage.blockContent = newContent
                      return {
                        buffer,
                        from: streamFrom,
                        to: streamTo,
                      }
                    }

                    try {
                      const tr = currentState.tr
                      applyTwoLevelDiff(
                        tr,
                        currentState,
                        previousContent,
                        newContent,
                        this.storage.blockPath!,
                        {}
                      )
                      await queueTransaction(() => {
                        const freshDispatch = editor.view.dispatch
                        // Don't set selection during streaming to prevent auto-selection of updated content
                        freshDispatch(tr)
                      })

                      this.storage.blockChunks = buffer
                      this.storage.blockContent = newContent
                    } catch (error) {
                      // console.warn(
                      //   '❌ JSON smart diffing failed:',
                      //   error,
                      //   newContent,
                      //   previousContent
                      // )
                    }
                  }

                  // Update tracking
                  streamTo = streamFrom + buffer.length // Approximate
                } else {
                  // For non-JSON content, use general content parsing and direct handling

                  if (!hasReplacedRange && from !== to) {
                    // Replace the specified range first
                    await queueTransaction((freshState, freshDispatch) => {
                      let currentFrom = from
                      let currentTo = to
                      // Defensive: recalculate if at end of doc
                      if (currentFrom > freshState.doc.content.size) {
                        currentFrom = freshState.doc.content.size
                      }
                      if (currentTo > freshState.doc.content.size) {
                        currentTo = freshState.doc.content.size
                      }
                      if (currentFrom < 0) currentFrom = 0
                      if (currentTo < 0) currentTo = 0
                      if (currentFrom > currentTo) currentFrom = currentTo
                      const tr = freshState.tr.delete(currentFrom, currentTo)
                      freshDispatch(tr)
                    })
                    hasReplacedRange = true
                  }

                  let content: Fragment | Node | Node[] | null

                  if (transform) {
                    // Use custom transform function
                    const defaultTransform = (transformContent = buffer) =>
                      parseContent(transformContent, contentType, currentState)
                    if (!defaultTransform) {
                      return null
                    }
                    content = transform({
                      buffer,
                      partial,
                      editor,
                      defaultTransform,
                    })
                  } else {
                    // Use default content parsing

                    content = parseContent(buffer, contentType, currentState)
                  }

                  if (!content) {
                    return null
                  }

                  const tr = currentState.tr

                  if (buffer.length === partial.length) {
                    // First chunk - insert new content

                    let insertPos = streamFrom

                    if (Array.isArray(content)) {
                      content.forEach((node, index) => {
                        if (node) {
                          tr.insert(insertPos, node)
                          insertPos += node.nodeSize
                        } else {
                          console.warn(`⚠️ Skipped null node at index ${index}`)
                        }
                      })
                    } else if (content) {
                      tr.insert(insertPos, content)
                      insertPos += (content as Node).nodeSize
                    } else {
                      console.warn(
                        '⚠️ No content to insert (content is null/undefined)'
                      )
                    }

                    streamTo = insertPos
                  } else {
                    // Subsequent chunks - replace previous content

                    if (streamFrom < streamTo) {
                      tr.delete(streamFrom, streamTo)
                    } else {
                      console.warn(
                        '⚠️ No previous content to delete (streamFrom >= streamTo)'
                      )
                    }

                    let insertPos = streamFrom

                    if (Array.isArray(content)) {
                      content.forEach((node, index) => {
                        if (node) {
                          tr.insert(insertPos, node)
                          insertPos += node.nodeSize
                        } else {
                          console.warn(
                            `⚠️ Skipped null replacement node at index ${index}`
                          )
                        }
                      })
                    } else if (content) {
                      tr.insert(insertPos, content)
                      insertPos += (content as Node).nodeSize
                    } else {
                      console.warn(
                        '⚠️ No replacement content to insert (content is null/undefined)'
                      )
                    }

                    streamTo = insertPos
                  }

                  // Apply appendToChain if provided
                  let finalTr = tr
                  if (appendToChain) {
                    const chain = editor.chain()
                    appendToChain(chain)
                    finalTr = tr
                  } else {
                    // do nothing
                  }

                  // Queue the transaction to prevent race conditions
                  await queueTransaction(() => {
                    // Get the current state again right before dispatch
                    const freshState = editor.state
                    const freshDispatch = editor.view.dispatch

                    // Recreate transaction with fresh state if needed
                    if (freshState !== currentState) {
                      console.warn(
                        '🔄 State changed, recreating transaction with fresh state'
                      )
                      // For now, just dispatch the original transaction
                      // TODO: Implement transaction recreation if needed
                    }

                    // Don't set selection during streaming to prevent auto-selection of inserted content
                    freshDispatch(finalTr)
                  })
                }

                // Check if this is the final write (when buffer indicates completion)
                // Reset streaming flag when appropriate
                const result = {
                  buffer,
                  from: streamFrom,
                  to: streamTo,
                }
                
                return result
              },

              getWritableStream: () => {
                return new WritableStream({
                  write: (chunk) => {
                    const text =
                      typeof chunk === 'string'
                        ? chunk
                        : new TextDecoder().decode(chunk)
                    writeAPI.write({
                      partial: text,
                      contentType: defaultContentType,
                    })
                  },
                  close: () => {
                    // Stream complete - reset streaming flag
                    this.storage.isStreaming = false
                    this.storage.isStreamingJson = false
                    console.log('🔄 Stream complete')
                  },
                  abort: () => {
                    // Stream aborted - reset streaming flag
                    this.storage.isStreaming = false
                    this.storage.isStreamingJson = false
                    console.log('🔄 Stream aborted')
                  },
                })
              },
            }

            // Execute callback synchronously to avoid transaction conflicts
            callback(writeAPI)

            return true
          },
        applyAiSuggestion:
          (options: ApplyAiSuggestionOptions) =>
          ({ tr, dispatch }) => {
            if (dispatch) {
              const state = this.editor?.state
              const pluginState = state && aiSuggestionPluginKey.getState(state)
              const suggestion = pluginState?.suggestions.find(
                (s) => s.id === options.suggestionId
              )

              if (suggestion) {
                const replacement = options.replacementOptionId
                  ? suggestion.replacementOptions.find(
                      (o) => o.id === options.replacementOptionId
                    )
                  : suggestion.replacementOptions[0]

                if (replacement) {
                  const { from, to } = suggestion.deleteRange
                  const { addText } = replacement

                  // Actually perform the text replacement
                  tr.insertText(addText, from, to)
                  tr.setMeta(aiSuggestionPluginKey, {
                    applySuggestion: options,
                  })
                  dispatch(tr)
                }
              }
            }
            return true
          },
      }
    },
  })

// Smart diffing functions (adapted from Plate.js streamInsertPlateJson.ts)
function applyTwoLevelDiff(
  tr: any,
  state: any,
  previousNodes: any[],
  newNodes: any[],
  basePath: number[],
  options: any
): void {
  const prevLen = previousNodes.length
  const newLen = newNodes.length

  // Handle length differences first - remove excess nodes from the end
  if (newLen < prevLen) {
    // Find and remove nodes from the end (working backwards to maintain positions)
    for (let i = prevLen - 1; i >= newLen; i--) {
      const nodePos = findNodePosition(tr.doc, basePath[0], i)
      if (nodePos !== null) {
        const $pos = tr.doc.resolve(nodePos)
        if ($pos.nodeAfter) {
          tr.delete(nodePos, nodePos + $pos.nodeAfter.nodeSize)
        }
      }
    }
  }

  // Compare existing nodes and update as needed (only 2 levels deep - performance!)
  const commonLength = Math.min(prevLen, newLen)

  for (let i = 0; i < commonLength; i++) {
    const previousNode = previousNodes[i]
    const newNode = newNodes[i]

    // Apply simplified node comparison (avoids deep recursion)
    applySimplifiedNodeDiff(
      tr,
      state,
      previousNode,
      newNode,
      basePath[0],
      i,
      options
    )
  }

  // Add new nodes at the end

  if (newLen > prevLen) {
    const nodesToAdd = newNodes.slice(prevLen)
    // Calculate the end position after existing content
    let insertPos = basePath[0]

    // Find the end position by walking through existing nodes
    for (let i = 0; i < prevLen; i++) {
      const nodePos = findNodePosition(tr.doc, basePath[0], i)
      if (nodePos !== null) {
        const $pos = tr.doc.resolve(nodePos)
        if ($pos.nodeAfter) {
          insertPos = nodePos + $pos.nodeAfter.nodeSize
        }
      }
    }

    // Insert new nodes at the calculated end position
    nodesToAdd.forEach((nodeJson) => {
      const processedNode = applyOptionsToNode(nodeJson, options)
      const node = safeCreateNode(state, processedNode)
      if (node) {
        tr.insert(insertPos, node)
        insertPos += node.nodeSize // Update position for next node
      }
    })
  }
}

// Adapted from Plate.js applySimplifiedNodeDiff - only compares essential properties for performance
function applySimplifiedNodeDiff(
  tr: any,
  state: any,
  previousNode: any,
  newNode: any,
  basePos: number,
  nodeIndex: number,
  options: any
): boolean {
  // Quick type check
  if (getNodeType(previousNode) !== getNodeType(newNode)) {
    return replaceNodeAtIndex(tr, state, newNode, basePos, nodeIndex, options)
  }

  // Text node comparison
  if (isTextNode(previousNode) && isTextNode(newNode)) {
    if (
      previousNode.text !== newNode.text ||
      !isDeepEqualData(previousNode.marks || [], newNode.marks || [])
    ) {
      return replaceNodeAtIndex(tr, state, newNode, basePos, nodeIndex, options)
    }
    return false // No changes needed
  }

  // Element node comparison - only check essential properties (performance optimization)
  if (isElementNode(previousNode) && isElementNode(newNode)) {
    // Only use container diff for specific container types (adapted from Plate.js)
    const useContainerDiff = isContainerElement(newNode.type as string)

    if (useContainerDiff) {
      return applyContainerElementDiff(
        tr,
        state,
        previousNode,
        newNode,
        basePos,
        nodeIndex,
        options
      )
    } else {
      // Regular element - replace entire node
      return replaceNodeAtIndex(tr, state, newNode, basePos, nodeIndex, options)
    }
  }

  return false
}

// Adapted from Plate.js applyContainerElementDiff - smart container diffing with isDeepEqualData
function applyContainerElementDiff(
  tr: any,
  state: any,
  previousNode: any,
  newNode: any,
  basePos: number,
  nodeIndex: number,
  options: any
): boolean {
  // Check if element properties changed (excluding content) - SAME AS PLATE.JS
  const prevProps = getNodeProps(previousNode)
  const newProps = getNodeProps(newNode)
  const propsChanged = !isDeepEqualData(prevProps, newProps)

  const prevContent = previousNode.content || []
  const newContent = newNode.content || []
  const contentChanged = !isDeepEqualData(prevContent, newContent)

  // If neither properties nor content changed, nothing to do - SAME AS PLATE.JS
  if (!propsChanged && !contentChanged) {
    return false
  }

  // If properties changed, replace the whole node (simpler and safer) - SAME AS PLATE.JS
  if (propsChanged) {
    return replaceNodeAtIndex(tr, state, newNode, basePos, nodeIndex, options)
  }

  // Only content changed - apply content diff (performance optimization) - SAME AS PLATE.JS
  if (contentChanged) {
    return applyContentDiff(tr, state, newContent, basePos, nodeIndex, options)
  }

  return false
}

// Apply content-level diffing (similar to Plate.js applyChildrenDiff)
function applyContentDiff(
  tr: any,
  state: any,
  newContent: any[],
  basePos: number,
  nodeIndex: number,
  options: any
): boolean {
  // For simplicity in Tiptap, we'll replace the entire node if content changed
  // This is still more efficient than replacing the entire document
  const nodePos = findNodePosition(tr.doc, basePos, nodeIndex)
  if (nodePos !== null) {
    const $pos = tr.doc.resolve(nodePos)
    if ($pos.nodeAfter) {
      // Create new node with updated content
      const newNodeJson = {
        type: $pos.nodeAfter.type.name,
        content: newContent,
        ...$pos.nodeAfter.attrs,
      }
      const processedNode = applyOptionsToNode(newNodeJson, options)

      const newNode = safeCreateNode(state, processedNode)
      if (newNode.type === "Step" || newNode.type === "Screenshot") {
        console.log("Safe creating new node", newNode);
      }
      if (newNode) {
        tr.replaceWith(nodePos, nodePos + $pos.nodeAfter.nodeSize, newNode)
        return true
      }
    }
  }

  return false
}

// Helper to replace a node at a specific index
function replaceNodeAtIndex(
  tr: any,
  state: any,
  newNodeJson: any,
  basePos: number,
  nodeIndex: number,
  options: any
): boolean {
  const nodePos = findNodePosition(tr.doc, basePos, nodeIndex)
  if (nodePos !== null) {
    const $pos = tr.doc.resolve(nodePos)
    if ($pos.nodeAfter) {
      const processedNode = applyOptionsToNode(newNodeJson, options)

      const newNode = safeCreateNode(state, processedNode)

      if (newNode) {
        tr.replaceWith(nodePos, nodePos + $pos.nodeAfter.nodeSize, newNode)
        return true
      }
    }
  }

  return false
}

export default StreamContentExtension
